import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { KeyRound, Tags, Users, AlertOctagon, Clock3, CalendarClock, ArrowUpRight, Vault, CheckCircle2 } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { Link } from "react-router-dom";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

function metricValue(data, primary, fallback = null) {
  return data?.[primary] ?? (fallback ? data?.[fallback] : undefined) ?? 0;
}

function MetricCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-xs tracking-[0.1em] uppercase font-semibold text-gray-500">{label}</div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${accent}20`, color: accent }}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-4 font-display font-bold text-3xl text-[#1F1F24]">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-500" data-testid="dashboard-loading">Carregando dashboard...</div>;
  if (!data) return <div className="text-red-600">Erro ao carregar dashboard</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: "#FF8A00" }}>
          Painel principal
        </div>
        <h2 className="font-display font-bold text-3xl mt-1 text-[#1F1F24]">Dashboard</h2>
        <p className="text-gray-600 text-sm mt-1">Visão geral do repositório de credenciais e ativos digitais visíveis para você.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <MetricCard testId="metric-visible-credentials" icon={KeyRound} label="Credenciais visiveis" value={metricValue(data, "visible_credentials", "total_credentials")} accent="#FF8A00" />
        <MetricCard testId="metric-vaults" icon={Vault} label="Cofres" value={metricValue(data, "vaults", "total_groups")} accent="#A855F7" />
        <MetricCard testId="metric-categories" icon={Tags} label="Categorias" value={metricValue(data, "categories", "total_categories")} accent="#2F3138" />
        <MetricCard testId="metric-users" icon={Users} label="Usuarios" value={metricValue(data, "users", "total_users")} accent="#0EA5E9" />
        <MetricCard testId="metric-active" icon={CheckCircle2} label="Ativos" value={metricValue(data, "active_credentials")} accent="#16A34A" />
        <MetricCard testId="metric-expired" icon={AlertOctagon} label="Expirados" value={metricValue(data, "expired_credentials", "expired")} accent="#DC2626" />
        <MetricCard testId="metric-expiring-7" icon={Clock3} label="Expirando em 7 dias" value={metricValue(data, "expiring_7_days", "expiring_7")} accent="#F59E0B" />
        <MetricCard testId="metric-expiring-30" icon={CalendarClock} label="Expirando em 30 dias" value={metricValue(data, "expiring_30_days", "expiring_30")} accent="#FBBF24" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentList title="Criadas recentemente" items={data.recent_created} testId="list-recent-created" />
        <RecentList title="Atualizadas recentemente" items={data.recent_updated} testId="list-recent-updated" />
      </div>
    </div>
  );
}

function RecentList({ title, items, testId }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={testId}>
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-display font-semibold text-[#1F1F24]">{title}</h3>
        <Link to="/credenciais" className="text-xs text-gray-500 hover:text-[#FF8A00] flex items-center gap-1">
          Ver todas <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-sm text-gray-500 text-center">Nada por aqui ainda</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((c) => (
            <li key={c.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-gray-50">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#1F1F24] truncate">{c.name}</div>
                <div className="text-xs text-gray-500 truncate">
                  {c.group_name ? `${c.group_name} · ` : ""}{c.category_name || "—"} · {c.asset_type} · {formatDate(c.updated_at)}
                </div>
              </div>
              <StatusBadge status={c.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
